import { ɵɵCdkFixedSizeVirtualScroll, ɵɵCdkVirtualForOf, ɵɵCdkVirtualScrollViewport } from '@angular/cdk/overlay';
import { AfterViewInit, ChangeDetectionStrategy, Component, computed, ElementRef, inject, input, OnDestroy, OnInit, signal, ViewEncapsulation } from '@angular/core';
import { MatIconButton } from '@angular/material/button';
import { MAT_BUTTON_TOGGLE_DEFAULT_OPTIONS, MatButtonToggle, MatButtonToggleChange, MatButtonToggleGroup } from '@angular/material/button-toggle';
import { MatIcon } from '@angular/material/icon';
import { MatFormField, MatLabel, MatOption, MatSelect, MatSelectChange } from '@angular/material/select';
import { PushPipe } from '@ngrx/component';
import { Store } from '@ngrx/store';
import { FullscreenLayoutAreas } from '@src/assets/css/fullscreen.template.scss';
import { ModalService } from '@src/suite/common/components/modal/modal.service';
import { CARD_WIDTH_LOWER_BREAKPOINTS } from '@src/suite/common/enums/constants';
import { CardViewType, SearchType } from '@src/suite/common/enums/enums';
import { NavigationDirection } from '@src/suite/common/enums/navigation-direction';
import { ObjectTypes } from '@src/suite/common/enums/object-types';
import { OpenFullscreenModalActions, SidecarEfffectActions, SidecarMenu } from '@src/suite/common/store/fullscreen';
import {
    selectSavedTopicViewStates,
    selectSidecarCurrentTopicSelectedAssignment,
    selectSidecarCurrentTopicSelectedStory,
    selectSidecarCurrentTopicStoryGuids,
    selectSidecarCurrentTopicVS,
    selectSidecarSelectedTopic,
    selectSidecarSelectedTopicListSearchType,
    selectTopics,
} from '@src/suite/common/store/fullscreen/selectors/sidecar.selectors';
import { selectI18N, selectTranslatedKey } from '@src/suite/common/store/i18n/selectors/i18n.selectors';
import { I18nKeys } from '@src/suite/common/utils/i18n.default';

import { ClickStopPropagationDirective } from '../../directives/stop-propagation';
import { AssignmentFullScreenDialogComponent } from '../../st-full-screen-dialog/assignment-full-screen/assignment-full-screen';
import { StoryFullScreenComponent } from '../../st-full-screen-dialog/stories-full-screen/story-full-screen/story-full-screen';
import { TopicFullScreenDialogComponent } from '../../st-full-screen-dialog/topic-full-screen/topic-full-screen';
import { SimpleSearchBoxAutocompleteComponent } from '../search-box/simple-search-box-autocomplete/simple-search-box-autocomplete';
import { StTopicItemComponent } from '../topic-item/st-topic-item.component';

@Component({
    selector: 'st-topic-list-sidecar',
    imports: [
        AssignmentFullScreenDialogComponent,
        ClickStopPropagationDirective,
        MatButtonToggle,
        MatButtonToggleGroup,
        MatFormField,
        MatIcon,
        MatIconButton,
        MatLabel,
        MatOption,
        MatSelect,
        ɵɵCdkFixedSizeVirtualScroll,
        ɵɵCdkVirtualForOf,
        ɵɵCdkVirtualScrollViewport,
        PushPipe,
        SimpleSearchBoxAutocompleteComponent,
        StoryFullScreenComponent,
        StTopicItemComponent,
        TopicFullScreenDialogComponent,
    ],
    templateUrl: './topic-list-sidecar.html',
    styleUrl: './topic-list-sidecar.scss',
    providers: [{ provide: MAT_BUTTON_TOGGLE_DEFAULT_OPTIONS, useValue: { hideSingleSelectionIndicator: false } }],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: {
        class: 'st-topic-list-sidecar',
    },
})
export class TopicListSidecarDialogComponent implements OnInit, AfterViewInit, OnDestroy {
    SearchType = SearchType;
    NavigationDirection = NavigationDirection;
    ObjectTypesEnum = ObjectTypes;
    CardViewType = CardViewType;

    modalService = inject(ModalService);
    store = inject(Store);
    elmRef = inject(ElementRef);

    readonly sidecarMenu = input<FullscreenLayoutAreas>();

    private containerResizeObserver: ResizeObserver;
    public readonly searchTerm = signal('');
    readonly cardViewType = signal(CardViewType.LARGE);

    readonly selectedListSearchType = computed(() => {
        const selectedListSearchType = this.selectedListSearchTypeInternal();
        if (!selectedListSearchType) {
            return SearchType.SAVED;
        }
        return selectedListSearchType;
    });
    readonly itemSize = computed(() => {
        if (this.cardViewType() === CardViewType.SMALL) {
            return 142;
        } else {
            return 138;
        }
    });
    readonly selectedListSearchTypeInternal = computed(() => this.store.selectSignal(selectSidecarSelectedTopicListSearchType(this.sidecarMenu()))());
    readonly savedViewDropdownVisible = computed(() => this.selectedListSearchType() === SearchType.SAVED);
    readonly selectTopics = computed(() => this.store.selectSignal(selectTopics(this.sidecarMenu()))());
    readonly selectSavedViewStates = computed(() => this.store.selectSignal(selectSavedTopicViewStates(this.sidecarMenu()))());
    readonly currentViewState = computed(() => this.store.selectSignal(selectSidecarCurrentTopicVS(this.sidecarMenu()))());
    readonly topicsLabel$ = this.store.select(selectTranslatedKey(I18nKeys.PSTWebTopicsLabel));
    readonly languageSettings = this.store.selectSignal(selectI18N)();
    readonly currentSelectedTopic = computed(() => this.store.selectSignal(selectSidecarSelectedTopic(this.sidecarMenu()))());
    readonly currentTopicSelectedStory = computed(() => this.store.selectSignal(selectSidecarCurrentTopicSelectedStory(this.sidecarMenu()))());
    readonly currentTopicSelectedAssignment = computed(() => this.store.selectSignal(selectSidecarCurrentTopicSelectedAssignment(this.sidecarMenu()))());

    readonly currentChild = computed(() => this.currentTopicSelectedStory() || this.currentTopicSelectedAssignment());
    readonly currentChildList = computed(() => {
        const parent = this.currentSelectedTopic();
        const currentChild = this.currentChild();
        const objType = currentChild?.Type;
        const guids = objType === ObjectTypes.SCRIPT ? this.store.selectSignal(selectSidecarCurrentTopicStoryGuids(this.sidecarMenu()))() : parent?.Sequence;
        return guids;
    });

    readonly indexInParentToShow = computed(() => {
        const parent = this.currentSelectedTopic();
        const currentChild = this.currentChild();
        const guids = this.currentChildList();

        if (parent && guids) {
            const indexInParent = guids?.findIndex(item => currentChild.Guid === item);
            if (indexInParent == null) {
                return 0;
            }
            return indexInParent + 1;
        }
    });

    readonly totalCount = computed(() => this.currentChildList()?.length || 0);
    readonly isSideCarMenuSelected = computed(() => {
        return this.sidecarMenu();
    });

    ngOnInit() {
        if (!this.selectSavedViewStates()) {
            this.store.dispatch(SidecarEfffectActions.loadSidecarTopicViewStates({ sidecarType: this.sidecarMenu() }));
        }
    }

    ngAfterViewInit(): void {
        this.containerResizeObserver = new ResizeObserver(entries => {
            const width = entries?.[0]?.contentRect?.width;
            if (width) {
                if (width < CARD_WIDTH_LOWER_BREAKPOINTS.medium) {
                    this.cardViewType.set(CardViewType.SMALL);
                } else if (width < CARD_WIDTH_LOWER_BREAKPOINTS.large) {
                    this.cardViewType.set(CardViewType.MEDIUM);
                } else {
                    this.cardViewType.set(CardViewType.LARGE);
                }
            }
        });

        this.containerResizeObserver?.observe(this.elmRef?.nativeElement);
    }

    ngOnDestroy(): void {
        this.containerResizeObserver?.unobserve(this.elmRef?.nativeElement);
        this.containerResizeObserver?.disconnect();
    }

    public async toggleSearchTab(event: MatButtonToggleChange) {
        this.store.dispatch(SidecarEfffectActions.setTopicsListSearchType({ searchType: event.value, sidecarType: this.sidecarMenu() }));
        if (this.selectedListSearchType() === SearchType.SEARCH) {
            this.store.dispatch(SidecarEfffectActions.clearSearchTopics({ sidecarType: this.sidecarMenu() }));
        }
        if (this.savedViewDropdownVisible()) {
            // if we are switching from search to saved views, and there is a saved view selected
            // we need to prefetch again
            this.searchTerm.set('');
            this.store.dispatch(SidecarEfffectActions.reloadSidecarTopicsBasedOnCurrentViewState({ sidecarType: this.sidecarMenu() }));
        }
    }

    public onSearchHandler(value: string | number) {
        this.store.dispatch(SidecarEfffectActions.searchTopics({ searchTerm: value.toString(), sidecarType: this.sidecarMenu() }));
    }

    public trackByGuid(_index, node) {
        if (node) {
            return node?.Guid;
        }
    }

    onViewStateGuidChanged(viewState: MatSelectChange) {
        this.store.dispatch(SidecarEfffectActions.setSidecarTopicCurrentViewState({ guid: viewState.value, sidecarType: this.sidecarMenu() }));
    }

    onFullscreenHandler(data: StorytellingRecord) {
        this.store.dispatch(SidecarEfffectActions.openCurrentTopic({ recordGuid: data.Guid, sidecarMenu: this.sidecarMenu() }));
    }

    onAssignmentFromCardHandler(parentData: StorytellingRecord, assignment: StorytellingAssignmentRecord) {
        this.onFullscreenHandler(parentData);
        this.onAssignmentOpenHandler(assignment);
    }

    public async onNewTopicClicked() {
        this.store.dispatch(
            OpenFullscreenModalActions.initCreateNewRecordFullscreen({
                recordType: ObjectTypes.TOPIC,
            })
        );
    }

    onAssignmentOpenHandler(record: StorytellingAssignmentRecord) {
        this.store.dispatch(SidecarEfffectActions.setCurrentTopicSelectedAssignment({ guid: record.Guid, sidecarMenu: this.sidecarMenu() }));
    }

    onStoryOpenHandler(story: StorytellingStoryRecord) {
        this.store.dispatch(
            SidecarEfffectActions.openStoryInSidecar({
                guid: story.Guid,
                sidecarType: this.sidecarMenu(),
                sidecarMenu: SidecarMenu.TOPICS,
            })
        );
    }

    public navigate(direction: NavigationDirection, recordType: ObjectTypes) {
        if (recordType === ObjectTypes.SCRIPT) {
            this.store.dispatch(SidecarEfffectActions.navigateStoryInCurrentTopic({ direction, sidecarMenu: this.sidecarMenu() }));
        } else if (recordType === ObjectTypes.ASSIGNMENT) {
            this.store.dispatch(SidecarEfffectActions.navigateAssignmentInCurrentTopic({ direction, sidecarMenu: this.sidecarMenu() }));
        }
    }
}
